/**
 * Globaler CSRF-Schutz für window.fetch
 *
 * Viele Services nutzen rohes fetch() statt der zentralen api.service-Schicht.
 * Damit diese Aufrufe das CSRF-Token automatisch mitsenden, patchen wir
 * window.fetch EINMAL beim App-Start. Der Wrapper hängt den
 * X-CSRF-Token-Header nur an schreibende (POST/PUT/PATCH/DELETE),
 * gleich-origin gerichtete API-Requests an.
 */

let currentCsrfToken: string | null = null;

/** Vom api.service aufgerufen, sobald ein neues Token vorliegt. */
export function setGlobalCsrfToken(token: string | null): void {
  currentCsrfToken = token;
}

export function getGlobalCsrfToken(): string | null {
  return currentCsrfToken;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isSameOriginApiRequest(url: string): boolean {
  try {
    // Relative URLs (z.B. "/api/v1/...") sind immer same-origin
    if (url.startsWith('/')) return url.startsWith('/api/');
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

let installed = false;

/** Einmalig beim App-Start aufrufen. */
export function installCsrfFetchInterceptor(): void {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.toString()
        : input.url;

    if (currentCsrfToken && MUTATING_METHODS.has(method) && isSameOriginApiRequest(url)) {
      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      if (!headers.has('X-CSRF-Token')) {
        headers.set('X-CSRF-Token', currentCsrfToken);
      }
      return originalFetch(input, { ...init, headers, credentials: init?.credentials || 'include' });
    }

    return originalFetch(input, init);
  };

  installCsrfXhrInterceptor();
}

/**
 * Dasselbe für XMLHttpRequest: axios nutzt im Browser XHR, nicht fetch.
 * Ohne diesen Teil schickten alle axios-Instanzen (u. a. Termine) kein
 * Token mit und scheiterten mit 403 „Ungültiges CSRF Token".
 * Auf XHR-Ebene greift es für jede Instanz, auch für bereits erzeugte.
 */
function installCsrfXhrInterceptor(): void {
  if (typeof XMLHttpRequest === 'undefined') return;

  const proto = XMLHttpRequest.prototype as XMLHttpRequest & {
    __csrfMethod?: string;
    __csrfUrl?: string;
    __csrfGesetzt?: boolean;
  };
  const originalOpen = proto.open;
  const originalSetRequestHeader = proto.setRequestHeader;
  const originalSend = proto.send;

  proto.open = function (this: typeof proto, method: string, url: string | URL, ...rest: any[]) {
    this.__csrfMethod = String(method).toUpperCase();
    this.__csrfUrl = String(url);
    this.__csrfGesetzt = false;
    return (originalOpen as any).call(this, method, url, ...rest);
  } as typeof proto.open;

  proto.setRequestHeader = function (this: typeof proto, name: string, value: string) {
    if (name.toLowerCase() === 'x-csrf-token') this.__csrfGesetzt = true;
    return originalSetRequestHeader.call(this, name, value);
  };

  proto.send = function (this: typeof proto, body?: Document | XMLHttpRequestBodyInit | null) {
    if (
      currentCsrfToken &&
      !this.__csrfGesetzt &&
      this.__csrfMethod && MUTATING_METHODS.has(this.__csrfMethod) &&
      this.__csrfUrl && isSameOriginApiRequest(this.__csrfUrl)
    ) {
      originalSetRequestHeader.call(this, 'X-CSRF-Token', currentCsrfToken);
    }
    return originalSend.call(this, body);
  };
}
